/**
 * Worked hours and short days (BR-155), for display.
 *
 * The figures come from the record, measured at check-out against the rules in
 * force then. They are never recomputed here against today's settings, which
 * may have changed since — the day was judged by the standard it was worked to.
 */

export interface ClosedDayFigures {
  durationMinutes: number | null;
  shortByMinutes: number | null;
  leftEarlyByMinutes: number | null;
}

/** 62 → "1h 2m". Zero or less reads "0m". */
export function formatDuration(mins: number): string {
  if (mins <= 0) return "0m";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/**
 * Why a day counts as short, e.g. `["6h 28m short", "left 6h 58m early"]`.
 *
 * Empty when the day met both measures — and when it was never measured. A
 * `null` figure means "not evaluated" (nothing configured, or closed before
 * the figures were kept), and must read as neither a shortfall nor "met".
 */
export function shortDayReasons(d: ClosedDayFigures): string[] {
  const reasons: string[] = [];
  if ((d.shortByMinutes ?? 0) > 0) reasons.push(`${formatDuration(d.shortByMinutes)} short`);
  if ((d.leftEarlyByMinutes ?? 0) > 0) reasons.push(`left ${formatDuration(d.leftEarlyByMinutes)} early`);
  return reasons;
}

/**
 * The minimum a short day was held to: what was worked plus how far short it
 * fell. Only knowable when it did fall short — a day that met its minimum does
 * not record what the minimum was, and guessing from today's settings could
 * quote a standard the day was never held to.
 */
export function minimumHeldTo(d: ClosedDayFigures): number | null {
  const short = d.shortByMinutes ?? 0;
  return short > 0 ? (d.durationMinutes ?? 0) + short : null;
}

/**
 * The detail panel's "Hours worked": e.g. value "1h 2m of 7h 30m minimum",
 * detail "6h 28m short · left 6h 58m early". An open day has no hours to judge
 * yet — its running total is not a day's worth of anything.
 */
export function hoursWorkedSummary(
  d: ClosedDayFigures & { checkOutAt: string | null },
): { value: string; detail: string | null; short: boolean } {
  if (!d.checkOutAt || d.durationMinutes == null) {
    return { value: "Still checked in", detail: null, short: false };
  }
  const minimum = minimumHeldTo(d);
  const reasons = shortDayReasons(d);
  return {
    value:
      minimum != null
        ? `${formatDuration(d.durationMinutes)} of ${formatDuration(minimum)} minimum`
        : formatDuration(d.durationMinutes),
    detail: reasons.length > 0 ? reasons.join(" · ") : null,
    short: reasons.length > 0,
  };
}
